import React from 'react';
import { motion } from 'framer-motion';

const KnowledgeBaseMockup = () => (
  <div className="aspect-video bg-muted rounded-xl border border-border p-6 flex flex-col gap-3 pointer-events-none group-hover:border-primary/20 transition-colors duration-300">
    {[
      { color: 'bg-primary/30', label: 'product-guide.pdf' },
      { color: 'bg-green-500/30', label: 'FAQ.txt' },
      { color: 'bg-amber-500/30', label: 'pricing.md' },
      { color: 'bg-sky-500/30', label: 'onboarding-checklist.docx' },
    ].map(({ color, label }, i) => (
      <motion.div
        key={label}
        initial={{ opacity: 0, x: -8 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.1 + i * 0.08, duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="flex items-center gap-3 bg-background rounded-lg px-4 py-3 border border-border"
      >
        <span className={`w-4 h-4 rounded ${color} shrink-0`} />
        <span className="text-sm text-foreground truncate">{label}</span>
      </motion.div>
    ))}
  </div>
);

const AIChatMockup = () => (
  <div className="aspect-video bg-background rounded-xl border border-border p-6 flex flex-col gap-3 justify-center pointer-events-none group-hover:border-primary/20 transition-colors duration-300">
    {[
      { side: 'right', text: 'How do I upgrade my plan?', delay: 0.1 },
      { side: 'left', text: 'You can upgrade from your dashboard under Settings → Billing. Choose a plan and you\'ll be charged immediately.', delay: 0.25 },
      { side: 'right', text: 'Does it prorate the cost?', delay: 0.4 },
    ].map(({ side, text, delay }) => (
      <motion.div
        key={text}
        initial={{ opacity: 0, y: 6 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay, duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className={`flex ${side === 'right' ? 'justify-end' : 'justify-start'}`}
      >
        <span className={`${side === 'right' ? 'bg-primary text-primary-foreground rounded-tr-sm' : 'bg-muted text-foreground rounded-tl-sm'} rounded-2xl px-4 py-2 text-sm max-w-[75%]`}>
          {text}
        </span>
      </motion.div>
    ))}
  </div>
);

const EmbedWidgetMockup = () => (
  <div className="aspect-video bg-foreground/5 rounded-xl border border-border p-6 font-mono text-sm flex items-center pointer-events-none overflow-hidden group-hover:border-primary/20 transition-colors duration-300">
    <motion.pre
      className="leading-relaxed"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ delay: 0.1, duration: 0.5 }}
    >
      <span className="text-primary">{'<script'}</span>
      {'\n  '}
      <span className="text-foreground/70">src</span>
      <span className="text-primary">{'='}</span>
      <span className="text-muted-foreground">{'"https://cdn.chytbot.com/widget.js"'}</span>
      {'\n  '}
      <span className="text-foreground/70">data-bot-id</span>
      <span className="text-primary">{'='}</span>
      <span className="text-muted-foreground">{'"your-bot-id"'}</span>
      {'\n'}
      <span className="text-primary">{'></script>'}</span>
    </motion.pre>
  </div>
);

const mockups: Record<string, React.ReactNode> = {
  'Knowledge Base': <KnowledgeBaseMockup />,
  'AI Chat': <AIChatMockup />,
  'Embed Widget': <EmbedWidgetMockup />,
};

const rows = [
  {
    label: 'Knowledge Base',
    imageFirst: true,
    headline: 'Build a chatbot that actually knows your business',
    body: 'Upload your documentation, FAQs, help articles, and product guides. Your chatbot learns from your real content and answers with accuracy — not hallucinations.',
  },
  {
    label: 'AI Chat',
    imageFirst: false,
    headline: 'Instant answers, any time, in your voice',
    body: 'Claude-powered AI responds to customer questions 24/7. Train it once, and it handles support without you lifting a finger.',
  },
  {
    label: 'Embed Widget',
    imageFirst: true,
    headline: 'On your site in under a minute',
    body: 'Copy one script tag and paste it anywhere. Works on Shopify, WordPress, Wix, Webflow, or any custom HTML page.',
  },
];

export const FeatureRows = () => {
  return (
    <section className="py-16 md:py-24">
      <div className="container">
        <div className="divide-y divide-border">
          {rows.map((row, index) => (
            <div
              key={row.label}
              className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center py-12 md:py-16"
            >
              {/* Visual column */}
              <motion.div
                className={`group ${row.imageFirst ? '' : 'md:order-last'}`}
                initial={{ opacity: 0, x: row.imageFirst ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.5, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
              >
                {mockups[row.label]}
              </motion.div>

              {/* Copy column */}
              <motion.div
                className={row.imageFirst ? '' : 'md:order-first'}
                initial={{ opacity: 0, x: row.imageFirst ? 20 : -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.5, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              >
                <p className="text-sm font-bold text-primary uppercase tracking-widest mb-3">
                  {row.label}
                </p>
                <h3 className="font-display text-3xl md:text-4xl font-bold text-foreground tracking-tight mb-4">
                  {row.headline}
                </h3>
                <p className="text-base text-muted-foreground leading-relaxed">{row.body}</p>
              </motion.div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
